import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["blocksContainer", "block", "placeholder"]
  static values = {
    postId: Number
  }

  connect() {
    this.initializeSortable()
  }

  disconnect() {
    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  blocksContainerTargetConnected() {
    this.initializeSortable()
  }

  initializeSortable() {
    // Destroy existing instance if it exists
    if (this.sortable) {
      this.sortable.destroy()
    }

    if (this.hasBlocksContainerTarget) {
      this.sortable = Sortable.create(this.blocksContainerTarget, {
        handle: '.drag-handle',
        animation: 150,
        ghostClass: 'block-ghost',
        dragClass: 'block-dragging',
        onEnd: this.handleReorder.bind(this)
      })
    }
  }

  handleReorder(event) {
    const blockIds = this.blockTargets.map(block => block.dataset.blockId)

    // Auto-save the new order
    fetch(`/admin/posts/${this.postIdValue}/blocks/reorder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ block_ids: blockIds })
    })
  }

  addBlock(event) {
    const placeholder = event.currentTarget
    const position = parseInt(placeholder.dataset.position)

    this.createBlock('text', position)
  }

  handlePlaceholderDragOver(event) {
    event.preventDefault()
    event.currentTarget.classList.add('drag-over')
  }

  handlePlaceholderDragLeave(event) {
    event.currentTarget.classList.remove('drag-over')
  }

  handlePlaceholderDrop(event) {
    event.preventDefault()
    event.currentTarget.classList.remove('drag-over')

    const files = Array.from(event.dataTransfer.files)
    const placeholder = event.currentTarget
    const position = parseInt(placeholder.dataset.position)

    if (files.length > 0) {
      const firstFile = files[0]

      if (firstFile.type.startsWith('video/')) {
        // Only handle single video
        this.createVideoBlock(position, firstFile)
      } else if (firstFile.type.startsWith('image/')) {
        // Handle multiple images
        this.createImageBlock(position, files.filter(f => f.type.startsWith('image/')))
      }
    }
  }

  createBlock(blockType, position) {
    fetch(`/admin/posts/${this.postIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: JSON.stringify({
        block: {
          block_type: blockType,
          position: position
        }
      })
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error adding block:', error)
    })
  }

  createImageBlock(position, files) {
    const formData = new FormData()
    formData.append('block[block_type]', 'image_container')
    formData.append('block[position]', position)

    files.forEach((file, index) => {
      formData.append(`images[${index}][file]`, file)
    })

    fetch(`/admin/posts/${this.postIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: formData
    })
    .then(response => response.text())
    .then(html => {
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error creating image block:', error)
    })
  }

  deleteBlock(event) {
    event.preventDefault()
    const blockElement = event.target.closest('[data-blocks-target="block"]')
    const blockId = blockElement.dataset.blockId

    if (confirm('Are you sure you want to delete this block?')) {
      fetch(`/admin/posts/${this.postIdValue}/blocks/${blockId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      })
      .then(() => {
        blockElement.remove()
      })
    }
  }

  createVideoBlock(position, file) {
    // Create placeholder UI for video upload
    const placeholderId = `video-upload-${Date.now()}`
    const placeholderHTML = `
      <div id="${placeholderId}" class="content-block card mb-3" data-blocks-target="block" data-block-id="uploading">
        <div class="card-body">
          <div class="d-flex align-items-center">
            <div class="spinner-border spinner-border-sm me-2" role="status">
              <span class="visually-hidden">Uploading...</span>
            </div>
            <div class="flex-grow-1">
              <div class="mb-1"><strong>Uploading video...</strong></div>
              <div class="progress" style="height: 4px;">
                <div class="progress-bar" role="progressbar" style="width: 0%" id="${placeholderId}-progress"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    // Insert placeholder at the correct position
    const placeholderElement = this.placeholderTargets.find(p => parseInt(p.dataset.position) === position + 1)
    if (placeholderElement) {
      placeholderElement.insertAdjacentHTML('beforebegin', placeholderHTML)
    } else {
      this.blocksContainerTarget.insertAdjacentHTML('beforeend', placeholderHTML)
    }

    const uploadElement = document.getElementById(placeholderId)
    const progressBar = document.getElementById(`${placeholderId}-progress`)

    // Get the direct upload URL from meta tag
    const uploadUrl = document.querySelector('meta[name="direct-upload-url"]')?.content || '/rails/active_storage/direct_uploads'

    // Upload the file using Direct Upload
    const upload = new DirectUpload(file, uploadUrl, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          const progress = (event.loaded / event.total) * 100
          progressBar.style.width = `${progress}%`
        })
      }
    })

    upload.create((error, blob) => {
      if (error) {
        console.error('Upload error:', error)
        uploadElement.innerHTML = `
          <div class="card-body">
            <div class="alert alert-danger mb-0">
              Upload failed: ${error}
            </div>
          </div>
        `
      } else {
        // Show save button UI
        uploadElement.innerHTML = `
          <div class="card-body">
            <div class="mb-3">
              <video controls style="max-width: 100%; max-height: 300px;">
                <source src="${URL.createObjectURL(file)}" type="${file.type}">
              </video>
            </div>
            <div class="mb-3">
              <label class="form-label small mb-1">Video Name</label>
              <input type="text"
                     class="form-control form-control-sm"
                     placeholder="Enter video name..."
                     id="${placeholderId}-name">
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <small class="text-muted">Video uploaded - click Save to add to post</small>
              <div class="btn-group">
                <button class="btn btn-sm btn-primary" data-action="click->blocks#saveVideoBlock" data-signed-id="${blob.signed_id}" data-position="${position}" data-placeholder-id="${placeholderId}">
                  <i class="bi bi-check-lg"></i> Save
                </button>
                <button class="btn btn-sm btn-secondary" data-action="click->blocks#cancelVideoUpload" data-placeholder-id="${placeholderId}">
                  <i class="bi bi-x-lg"></i> Cancel
                </button>
              </div>
            </div>
          </div>
        `
      }
    })
  }

  saveVideoBlock(event) {
    const button = event.currentTarget
    const signedId = button.dataset.signedId
    const position = parseInt(button.dataset.position)
    const placeholderId = button.dataset.placeholderId

    // Get video name from input
    const nameInput = document.getElementById(`${placeholderId}-name`)
    const videoName = nameInput?.value || ''

    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Saving...'

    const formData = new FormData()
    formData.append('block[block_type]', 'video_container')
    formData.append('block[position]', position)
    formData.append('video_signed_id', signedId)
    formData.append('video_name', videoName)

    fetch(`/admin/posts/${this.postIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html'
      },
      body: formData
    })
    .then(response => response.text())
    .then(html => {
      // Remove the placeholder
      document.getElementById(placeholderId)?.remove()
      // Render the Turbo Stream response
      Turbo.renderStreamMessage(html)
    })
    .catch(error => {
      console.error('Error creating video block:', error)
      button.disabled = false
      button.innerHTML = '<i class="bi bi-check-lg"></i> Save'
    })
  }

  cancelVideoUpload(event) {
    const placeholderId = event.currentTarget.dataset.placeholderId
    document.getElementById(placeholderId)?.remove()
  }
}
